import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react"
import { Box, CellContentPosition, TableProps, ConfirmationDialog, Table, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { useSelection } from "@tm/utils"
import { Note, VatRateSelectionItem } from "@tm/models"
import { useAfterPaintEffect } from "@bundles/parts/components/ListV2/helpers"
import { CustomArticleItemResponse, useDeleteCustomArticles, useMoveCustomArticles, useShowCustomArticleListItems } from "@tm/data/basket"
import { getBundleParams } from "@bundles/compilations/utils"
import { useArticleNotes } from "@bundles/compilations/hooks/useArticleNotes"
import { OwnArticleItem } from "./OwnArticleItem"
import { useListSettings } from "../useListSettings"
import { useOwnArticlesStore } from "../store"
import { TableLoading } from "../../../TableLoading"
import { ItemSelection } from "../../../ItemSelection"
import { OwnListSearchType } from "../models"
import { useAllCompilations } from "../useAllCompilations"

export type ArticleListHandle = {
    scrollToTop(): void
}

type OwnArticleListProps = {
    customPartVatRates?: VatRateSelectionItem[]
    onAddArticlesToBasket?(items: [CustomArticleItemResponse, number][]): void
    onReplaceArticle?(selected: CustomArticleItemResponse): void
    onEditArticle(article: CustomArticleItemResponse, note?: Note): void
}

export const OwnArticleList = forwardRef<ArticleListHandle, OwnArticleListProps>((props, ref) => {
    const { onEditArticle, onAddArticlesToBasket, onReplaceArticle } = props
    const { translateText } = useLocalization()
    const { searchQuery, selectedCompilation, compilations, selectedSearchType, categoryFilter } = useOwnArticlesStore()
    const { mutateAsync: moveArticles } = useMoveCustomArticles()
    const { mutateAsync: deleteArticles } = useDeleteCustomArticles()

    const { enableArticleNotes } = getBundleParams()
    const tableRef = useRef<HTMLDivElement>(null)
    const autoFetchIndex = useRef(0)
    const { showPurchasePrice } = useListSettings()

    const listRequest = useMemo<Parameters<typeof useShowCustomArticleListItems>[0]>(
        () => ({
            id: selectedCompilation?.id,
            query: selectedSearchType === OwnListSearchType.Article ? searchQuery : undefined,
            productGroupIdsFilter: categoryFilter?.id.toString(),
        }),
        [searchQuery, selectedCompilation, selectedSearchType, categoryFilter]
    )

    const {
        data: { pages } = {},
        fetchNextPage,
        isLoading,
        isFetchingNextPage,
        isFetched,
    } = useShowCustomArticleListItems(listRequest, !!compilations)
    const articles = useMemo(() => pages?.flatMap((x) => x.data) ?? [], [pages])
    const pageIndex = pages?.length ?? 0

    const getAllArticleIds = useCallback(() => articles?.map((article) => article.id) || [], [articles])
    const { selectedIds, toggleSelected, selectAll, unselectAll } = useSelection(getAllArticleIds)
    const [deleteIds, setDeleteIds] = useState<string[]>()
    const [quanities, setQuantities] = useState<Record<string, number>>({})

    const { articleNotes } = useArticleNotes(enableArticleNotes ? getAllArticleIds() : [])
    const allCompilations = useAllCompilations()

    useImperativeHandle(ref, () => {
        return {
            scrollToTop: () => {
                tableRef.current?.scrollTo({ top: 0 })
            },
        }
    })

    const findNoteByArticleId = useCallback((articleId?: string) => articleNotes.find((note) => note.noteId === articleId), [articleNotes])

    const handleEditArticle = useCallback(
        (article: CustomArticleItemResponse) => {
            onEditArticle(article, findNoteByArticleId(article.id))
        },
        [findNoteByArticleId, onEditArticle]
    )

    const handleAddToBasket = useCallback(
        (article: CustomArticleItemResponse, quantity: number) => {
            onAddArticlesToBasket?.([[article, quantity]])
        },
        [onAddArticlesToBasket]
    )

    const handleAddArticlesToBasket = useCallback(
        async (ids: string[]) => {
            const list: [CustomArticleItemResponse, number][] = []
            ids.forEach((id) => {
                const art = articles.find((x) => x.id === id)
                if (art) {
                    list.push([art, 1])
                }
            })
            await onAddArticlesToBasket?.(list)
        },
        [articles]
    )

    const handleToggleArticle = useCallback(
        (article: CustomArticleItemResponse) => {
            toggleSelected(article.id)
        },
        [selectedIds]
    )

    const handleDeleteArticles = useCallback(async (ids: string[]) => {
        setDeleteIds(ids)
    }, [])

    const handleDeleteArticle = useCallback((article: CustomArticleItemResponse) => {
        setDeleteIds([article.id])
    }, [])

    const handleDeleteConfirm = useCallback(async () => {
        if (deleteIds) {
            try {
                await deleteArticles({ ids: deleteIds })
            } finally {
                setDeleteIds(undefined)
                unselectAll()
            }
        }
    }, [articles, deleteIds, deleteArticles])

    const closeDeleteDialog = useCallback(() => {
        setDeleteIds(undefined)
    }, [])

    const handleChangeQuantity = useCallback((article: CustomArticleItemResponse, quantity: number) => {
        setQuantities((prev) => ({
            ...prev,
            [article.id]: quantity,
        }))
    }, [])

    const getQuantity = useCallback(
        (article: CustomArticleItemResponse) => {
            return quanities[article.id] ?? 1
        },
        [quanities]
    )

    const handleMoveArticles = useCallback(async (ids: string[], sourceId: string | undefined, targetId: string) => {
        try {
            await moveArticles({ idList: ids, customArticleListSourceId: sourceId, customArticleListTargetId: targetId })
        } finally {
            unselectAll()
        }
    }, [])

    useAfterPaintEffect(() => {
        const scrollContainer = tableRef.current
        if (
            !!articles.length &&
            pageIndex > autoFetchIndex.current &&
            scrollContainer &&
            scrollContainer.clientHeight === scrollContainer.scrollHeight &&
            document.body.clientHeight > scrollContainer.clientHeight // prevent endless loop loading problems
        ) {
            // auto fetch until no new pageIndex comes
            autoFetchIndex.current = pageIndex
            fetchNextPage()
        }
    }, [tableRef.current, articles, pageIndex])

    const tableData: TableProps = useMemo(() => {
        const data: TableProps = {
            scrollbar: "system",
            headerStyle: "default",
            headerBackground: "inherit",
            columns: [
                { header: translateText(13177), gridSize: "1fr" }, // Article Number
                { header: translateText(25), gridSize: "2fr" }, //  Description
                { header: translateText(158), gridSize: "2fr" }, // Category
                { header: translateText(55), gridSize: "1fr", alignContent: CellContentPosition.right }, // Purchase price
                { header: translateText(1620), gridSize: "1fr", alignContent: CellContentPosition.right }, // Retail price
                { header: translateText(706), gridSize: "1fr", alignContent: CellContentPosition.right, sx: { pr: 3 } }, // Vat
                { alignContent: CellContentPosition.right }, // actions
            ],
        }
        if (categoryFilter) {
            data.columns.splice(2, 1)
        }
        if (!showPurchasePrice) {
            data.columns.splice(3, 1)
        }
        return data
    }, [translateText, showPurchasePrice, categoryFilter])

    return (
        <Box minHeight="0" flex={1} position="relative">
            <ItemSelection
                positionType="article"
                selectedItemIds={selectedIds}
                lists={allCompilations}
                selectedListId={selectedCompilation?.id}
                allSelected={articles.every((x) => selectedIds.includes(x.id))}
                onDelete={handleDeleteArticles}
                onSelectAll={selectAll}
                onUnselectAll={unselectAll}
                onAddToBasket={onAddArticlesToBasket ? handleAddArticlesToBasket : undefined}
                onMove={handleMoveArticles}
            />
            {(isLoading || isFetchingNextPage) && <TableLoading colCount={tableData.columns.length} />}
            {isFetched && !articles.length && (
                <Typography sx={{ marginBlock: 1, textAlign: "center", gridColumn: `1 / span ${tableData.columns.length}` }}>
                    {translateText(13716)}
                </Typography>
            )}

            {isFetched && !!articles.length && (
                <Table {...tableData} onScrollBottom={fetchNextPage} ref={tableRef} overflowY="auto">
                    {articles.map((article) => (
                        <OwnArticleItem
                            key={article.id}
                            article={article}
                            quantity={getQuantity(article)}
                            note={findNoteByArticleId(article.id)}
                            isSelected={selectedIds.includes(article.id)}
                            isDeleting={!!deleteIds?.length}
                            onEditArticle={handleEditArticle}
                            onAddToBasket={handleAddToBasket}
                            onDeleteArticle={handleDeleteArticle}
                            onToggleArticle={handleToggleArticle}
                            onChangeQuantity={handleChangeQuantity}
                            onReplace={onReplaceArticle}
                            hideCategory={!!categoryFilter}
                        />
                    ))}
                </Table>
            )}
            <ConfirmationDialog
                open={!!deleteIds?.length}
                confirmationButtonText={translateText(585)}
                cancelButtonText={translateText(584)}
                onConfirm={handleDeleteConfirm}
                onCancel={closeDeleteDialog}
            >
                {translateText(833)}
            </ConfirmationDialog>
        </Box>
    )
})
